import { Box, Typography, Checkbox, FormControlLabel, Radio, RadioGroup, FormControl, FormLabel } from "@mui/material";
import { BlackButtonComponent, WhiteButtonComponent } from "../../components";
import useBusinessScreen from "./hooks/businessScreen";
import DynamicInput from "./feilds/dynamic-input";
import OrganisationDetailsForm from "./organisation-form/organisation-form";
import { useState } from "react";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import {
  resetOrganisationDetails,
  updateInitialState,
  updateOrganisationDetails,
} from "slice/merchantSlice";
import { fetchGST, fetchUdhyamDetails } from "services/otpService"; // Assuming fetchUdyam exists
import { RootState } from "store";
import { appendDetails } from "services/MerchantHostApi";
import { containsPropertyMerchant, formatBrandName, getChangedFields } from "utils/index";
import { useNavigate } from "react-router";

const BusinessDetailsScreen = () => {
  const getOrganisationDetails = useSelector(
    (state: RootState) => state.merchantForm.organisationDetails
  ) as any;
  const userDetails = useSelector(
    (state: RootState) => state.appLayout.userDetails
  ) as any;
  const isOrganisationVerified = getOrganisationDetails?.isOrganisationVerified;
  const { formik } = useBusinessScreen();
  const [loading, setLoading] = useState(false);
  const [allowFetchDetails, setAllowFetchDetails] = useState(false);
  const [initialValues, setInitialValues] = useState(formik.values);
  const [selectedOption, setSelectedOption] = useState<'gst' | 'udyam'>(getOrganisationDetails?.verificationType ||  'gst');
  const navigate = useNavigate();

  const dispatch = useDispatch();

  const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newOption = event.target.value as 'gst' | 'udyam';
    setSelectedOption(newOption);
    
    // Clear the form fields when switching options
    if (newOption === 'gst') {
      formik.setFieldValue('udhyamNumber', '');
      formik.setFieldValue('confirmUdhyamNumber', '');
    } else {
      formik.setFieldValue('gstNumber', '');
      formik.setFieldValue('confirmgstNumber', '');
    }
    
    // Reset verification state
    dispatch(resetOrganisationDetails());
    setAllowFetchDetails(false);
  };

  const validateInputs = () => {
  const gstRegex = /^[0-9A-Z]{15}$/;
  const udyamRegex = /^UDYAM-[A-Z]{2}-[0-9]{2}-\d{7}$/;

  if (selectedOption === "gst") {
    const { gstNumber, confirmgstNumber } = formik.values;

    if (!gstNumber || !confirmgstNumber) {
      toast.error("Please fill in both GST number fields.");
      return false;
    }

    if (gstNumber !== confirmgstNumber) {
      toast.error("GST numbers do not match.");
      return false;
    }

    if (!gstRegex.test(gstNumber)) {
      toast.error("Invalid GST format. It should be 15 alphanumeric characters (e.g., 27ABCDE1234F1Z5).");
      return false;
    }
  } else {
    const { udhyamNumber, confirmUdhyamNumber } = formik.values;

    if (!udhyamNumber || !confirmUdhyamNumber) {
      toast.error("Please fill in both Udyam number fields.");
      return false;
    }

    if (udhyamNumber !== confirmUdhyamNumber) {
      toast.error("Udyam numbers do not match.");
      return false;
    }

    if (!udyamRegex.test(udhyamNumber)) {
      toast.error("Invalid Udyam format. Expected format: UDYAM-XX-00-0000000.");
      return false;
    }
  }

  return true;
  };

  const handleProceedClick = async () => {
    if (!allowFetchDetails) {
      toast.error("Please allow fetching details before submitting.");
      return;
    }

    if (!validateInputs()) {
      return;
    }

    setLoading(true);
    try {
      let response;
      
      if (selectedOption === 'gst') {
        response = await fetchGST(formik.values.gstNumber);
      } else {
        response = await fetchUdhyamDetails(formik.values.udhyamNumber);
      }

      if (response === null || response.status !== 1) {
        toast.error(
          response?.message || `Invalid ${selectedOption.toUpperCase()} details. Please check your input.`
        );
        return;
      }

      dispatch(
        updateOrganisationDetails({
          ...response.result,
          isOrganisationVerified: true,
          verificationType: selectedOption, // Store which type was used for verification
        })
      );
      toast.success(`${selectedOption.toUpperCase()} details verified successfully!`);
    } catch (error) {
      console.error(`Error fetching ${selectedOption.toUpperCase()} details:`, error);
      toast.error(`Failed to fetch ${selectedOption.toUpperCase()} details. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    formik.resetForm();
    dispatch(resetOrganisationDetails());
    setAllowFetchDetails(false);
    setLoading(false);
    setSelectedOption('gst');
    toast.info("Form has been reset.");
  };

  const handleSubmit = async () => {
    if (!isOrganisationVerified) {
      toast.error("Please verify your organisation details before proceeding.");
      return;
    }
    if(formik.values.brandName === ""){
      toast.error("Please enter brand name");
      return;
    }
    try {
      setLoading(true);
      const changedFields = getChangedFields(initialValues, formik.values);
      const data:any = {
        organisationDetails: {
          ...getOrganisationDetails,
          cin: formik.values.cin,
          brandName: formatBrandName(formik.values.brandName),
          formatedAddress: formik.values.registeredAddress,
          updatedItems: changedFields,
          verificationType: getOrganisationDetails.verificationType, // Include verification type
          udhyamNumber: formik?.values?.udhyamNumber || undefined,
          gstNumber: formik?.values?.gstNumber || undefined,
        },
      };

      const val = containsPropertyMerchant(userDetails, "organisationDetails");
      
      if (val === "UPDATE") {
        data.kycStatus = "UPDATE";
      } else if (val === "TRUE") {
        data.kycStatus = "COMPLETED";
      }

      const response = await appendDetails(
        userDetails.userType,
        userDetails.userId,
        data
      );
      if (response?.message === "Item updated successfully") {
        toast.success("Merchant details updated successfully!");
        dispatch(updateInitialState(response?.updatedItem));
        setInitialValues(formik.values);
        navigate("/vendor-agreement");
        window.location.reload();
      } else {
        toast.error(
          response?.message || "Something went wrong. Please try again."
        );
      }
    } catch (error) {
      console.error("API Error:", error);
      toast.error(
        "Failed to update details. Please check your network and try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box display="flex" flexDirection="column" rowGap={2}>
      <Typography variant="h5" mb={2}>
        Organisation Business Details 
        <Typography
            variant="subtitle1"
            fontFamily={"Inter"}
            fontWeight={"500"}
            fontSize={16}
            display={"inline"}
            ml={1}
          >
            (For invoicing)
          </Typography>{" "}
      </Typography>

      <Box
        border={1}
        borderColor="rgba(0,0,0,0.2)"
        borderRadius={2.5}
        flex={1}
        p={3}
      >
        {!isOrganisationVerified && (
          <FormControl component="fieldset" sx={{ mb: 3 }}>
            <FormLabel component="legend" sx={{ mb: 1 }}>
              Choose verification method: (if you have gst number then choose GST)
            </FormLabel>
            <RadioGroup
              row
              value={selectedOption}
              onChange={handleOptionChange}
            >
              <FormControlLabel 
                value="gst" 
                control={<Radio />} 
                label="GST Number" 
              />
              <FormControlLabel 
                value="udyam" 
                control={<Radio />} 
                label="Udyam Number" 
              />
            </RadioGroup>
          </FormControl>
        )}

        {selectedOption === 'gst' && (
          <>
            <DynamicInput
              formik={formik}
              name="gstNumber"
              label="GST Number*"
              type="text"
              placeholder="Enter your GST Number"
              disabled={isOrganisationVerified}
            />

            <DynamicInput
              formik={formik}
              name="confirmgstNumber"
              label="Confirm GST Number*"
              type="text"
              placeholder="Re-enter your GST Number"
              disabled={isOrganisationVerified}
            />
          </>
        )}

        {selectedOption === 'udyam' && (
          <>
            <DynamicInput
              formik={formik}
              name="udhyamNumber"
              label="Udyam Number*"
              type="text"
              placeholder="Enter your Udyam Number"
              disabled={isOrganisationVerified}
            />

            <DynamicInput
              formik={formik}
              name="confirmUdhyamNumber"
              label="Confirm Udyam Number*"
              type="text"
              placeholder="Re-enter your Udyam Number"
              disabled={isOrganisationVerified}
            />
          </>
        )}

        {!isOrganisationVerified && (
          <FormControlLabel
            control={
              <Checkbox
                checked={allowFetchDetails}
                onChange={(e) => setAllowFetchDetails(e.target.checked)}
              />
            }
            label={`Allow fetching details using ${selectedOption.toUpperCase()} number`}
          />
        )}

        {!isOrganisationVerified && (
          <Box textAlign="right" display="flex" columnGap={2} mt={3}>
            <BlackButtonComponent
              onClick={handleProceedClick}
              btnText={loading ? "Verifying..." : "Verify Details"}
              sx={{ padding: "10px 40px" }}
              disabled={!allowFetchDetails || loading}
            />
          </Box>
        )}

        {isOrganisationVerified && <OrganisationDetailsForm formik={formik} />}
      </Box>

        { isOrganisationVerified &&
          <Box
            textAlign="left"
            display="flex"
            columnGap={2}
            mt={3}
            justifyContent="end"
            >
          <WhiteButtonComponent onClick={handleReset} btnText="Reset" />
          <BlackButtonComponent
            onClick={handleSubmit}
            btnText={loading ? "Submitting..." : "Submit"}
            disabled={loading || !isOrganisationVerified}
            />
        </Box>
        }
    </Box>
  );
};

export default BusinessDetailsScreen;